import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import PublishingState from 'components/Links/PublishingState';
import { OwnerItem } from 'components/Pickers/OwnerPicker';
import { SingleTag } from 'components/Tags/SingleTag';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { default as Activity, ActivityType } from 'models/activity';
import { IOwner } from 'models/owner';
import ResourceLink, { ResourceLinkPublishingState, ResourceLinkVersion } from 'models/resourceLink';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISitePage, IStandardWebPart } from 'services/Graph/SharepointInterfaces';
import { GetPageBannerWebPart, graphGetSitePageImage } from 'services/Graph/graphServicePage';
import logger from 'services/Logging/logService';
import { toLocaleDateShort } from 'utils/datetime';
import { newGuidNil } from 'utils/guid';
import NewVersionNotification from '../../Links/NewVersionNotification';
import { ImgBgState, spHeaderBgDark, spHeaderBgLight } from './SharePointPageRenderHelpers';

//
// Page header for SharePoint documents
//
interface IPageHeaderDocProps {
  link: ResourceLink;
  activity: Activity | undefined;
  forceLightMode?: boolean;
}

export const PageHeaderDoc = (props: IPageHeaderDocProps) => {
  const appContext = useContext(AppContext);
  const [versionInfo, setVersionInfo] = useState<ResourceLinkVersion | undefined>(undefined);

  const showActivity = props.activity && props.activity.typeOfActivity === ActivityType.LibraryNewVersion;
  const useDarkMode = props.forceLightMode ? false : appContext.useDarkMode;
  const imageSrc: string | undefined = useDarkMode ? spHeaderBgDark : spHeaderBgLight;

  useEffect(() => {
    //version
    if (props.link.versionInfo.hasData()) {
      setVersionInfo(props.link.versionInfo.clone());
    } else {
      loadMetaData(props.link);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.link]);

  const loadMetaData = async (link: ResourceLink) => {
    try {
      await link.getWebURL(appContext);
      setVersionInfo(link.versionInfo.clone());
    } catch {
      //ignore
    }
  };

  //Main render
  return (
    <IPStack.Item
      styles={{
        root: {
          height: 100,
          padding: 30,
          backgroundImage: `url(${imageSrc})`,
          backgroundSize: '100%',
          backgroundPositionY: 'center',
        },
      }}
      key={`sp-header-${props.link.linkId}`}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall} verticalAlign="end">
        {showActivity && <NewVersionNotification />}
        <IPStack.Item>
          <IPText variant="xxLarge">{props.link.linkName}</IPText>
        </IPStack.Item>
        {props.link.list.enableMetaData && (
          <PageHeaderVersion
            owner={props.link}
            publishingState={props.link.publishingState}
            versionInfo={versionInfo}
            hasCustomImage={false}
          />
        )}
      </IPStack>
    </IPStack.Item>
  );
};

//
// Page header for SharePoint pages
//
interface IPageHeaderPageProps {
  link: ResourceLink;
  owner?: IOwner;
  page: ISitePage;
  activity?: Activity;
  forceLightMode?: boolean;
  disableGradient?: boolean;
}

export const PageHeaderPage = (props: IPageHeaderPageProps) => {
  const appContext = useContext(AppContext);
  const [bgImageUri, setBgImageUri] = useState<string | undefined>(undefined);
  const [hasCustomImage, setHasCustomImage] = useState<boolean>(false);
  const [imgBgState, setImgBgState] = useState<ImgBgState>(ImgBgState.Initializing);
  const [webPart] = useState<IStandardWebPart | undefined>(GetPageBannerWebPart(props.page));

  const theme = getTheme();
  const useDarkMode = props.forceLightMode ? false : appContext.useDarkMode;
  const showActivity = props.activity && props.activity.typeOfActivity === ActivityType.LibraryNewVersion;
  const title = props.page.title || props.page.name;

  useEffect(() => {
    if (props.page) {
      loadBgImageAsync(props.page, webPart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webPart, props.page]);

  const loadBgImageAsync = async (page: ISitePage, webPart: IStandardWebPart | undefined) => {
    try {
      let uri: string | undefined = undefined;
      if (webPart) {
        setHasCustomImage(true);
        setImgBgState(ImgBgState.LoadingImage);
        uri = await graphGetSitePageImage(appContext, page, webPart);
      }
      if (!uri) {
        setDefaultImage();
      } else {
        setBgImageUri(uri);
      }
    } catch (err) {
      logger.debug('Error loading SharePoint page header image', err);
      setDefaultImage();
    } finally {
      setImgBgState(ImgBgState.Ready);
    }
  };

  const setDefaultImage = () => {
    const uri = useDarkMode ? spHeaderBgDark : spHeaderBgLight;
    setBgImageUri(uri);
    setHasCustomImage(false);
  };

  //set defaults
  let showTopicHeader = false;
  let topicHeader: string | undefined = undefined;
  let backgroundPosition = 'center center';
  let backgroundGradient: string = '';

  if (webPart) {
    //topic header
    showTopicHeader = webPart.data?.properties?.showTopicHeader ?? (false as boolean);
    topicHeader = webPart.data?.properties?.topicHeader as string | undefined;

    //move focus point up/down. x-position is fixed because image is resized to fit in width
    const translateY = webPart.data?.properties?.translateY ?? 0;
    if (translateY !== 0) {
      backgroundPosition = `center calc(50% + ${translateY - 50}%)`;
    }

    if (!props.disableGradient && webPart.data?.properties?.enableGradientEffect === true) {
      if (hasCustomImage) {
        //Only show gradient if a custom image is set
        //SharePoint does not apply gradient to default image
        backgroundGradient = 'linear-gradient(to bottom, rgba(0,0,0,0) 30%,rgba(0,0,0,0.6) 100%),';
      }
    }
  }

  // if a custom image is set, display text in white with shadow
  const textStyles = {
    root: {
      color: hasCustomImage ? theme.palette.white : undefined,
      textShadow: hasCustomImage ? '2px 1px 4px grey' : undefined,
    },
  };

  // Main render
  return (
    <IPStack.Item
      styles={{
        root: {
          height: 120,
          padding: 30,
          backgroundImage: `${backgroundGradient} url(${bgImageUri})`,
          backgroundSize: props.disableGradient ? '100%' : 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: backgroundPosition,
          opacity: imgBgState !== ImgBgState.Ready ? 0 : 1,
          transition: hasCustomImage ? 'opacity 0.5s ease' : undefined,
        },
      }}
      key={`sp-header-${props.page.id}`}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall} verticalAlign="end">
        <IPStack horizontal tokens={globalStackTokensGapMedium} verticalAlign="center">
          {showActivity && <NewVersionNotification />}
          {showTopicHeader && topicHeader && (
            <IPStack.Item>
              <SingleTag
                bold
                tag={{
                  tagId: newGuidNil(),
                  tagName: topicHeader.toUpperCase(),
                  tagColor: theme.palette.neutralQuaternary,
                }}
              />
            </IPStack.Item>
          )}
        </IPStack>
        <IPStack.Item>
          <IPText variant="xxLargePlus" styles={textStyles}>
            {title}
          </IPText>
        </IPStack.Item>
        {props.link.list.enableMetaData && (
          <PageHeaderVersion
            owner={props.owner}
            publishingState={props.link.publishingState}
            versionInfo={props.link.versionInfo}
            hasCustomImage={hasCustomImage}
          />
        )}
      </IPStack>
    </IPStack.Item>
  );
};

interface IPageHeaderVersionProps {
  owner?: IOwner;
  publishingState: ResourceLinkPublishingState | undefined;
  versionInfo: ResourceLinkVersion | undefined;
  hasCustomImage?: boolean;
}

export const PageHeaderVersion = (props: IPageHeaderVersionProps) => {
  const { t } = useTranslation(['translation', 'library']);
  const theme = getTheme();

  const textStyles = {
    root: {
      color: props.hasCustomImage ? theme.palette.white : undefined,
      textShadow: props.hasCustomImage ? '2px 1px 4px grey' : undefined,
    },
  };

  //Main render
  if (!props.versionInfo?.hasData() && !props.owner) {
    return null;
  }

  return (
    <IPStack.Item>
      <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="center">
        {props.owner && (
          <IPStack.Item>
            <OwnerItem owner={props.owner} textColor={props.hasCustomImage ? theme.palette.white : undefined} />
          </IPStack.Item>
        )}
        {props.versionInfo?.hasData() && (
          <Fragment>
            <IPStack.Item>
              <IPText styles={textStyles}>{t('library:DetailList.Header.Version')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <PublishingState
                iconColor={props.hasCustomImage ? theme.palette.white : undefined}
                textColor={props.hasCustomImage ? theme.palette.white : undefined}
                version={props.versionInfo?.version}
                publishingState={props.publishingState}
              />
            </IPStack.Item>
            <IPStack.Item styles={{ root: { paddingLeft: 10 } }}>
              <IPText styles={textStyles}>{t('library:Preview.SavedOn')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPText styles={textStyles}>{toLocaleDateShort(props.versionInfo?.date)}</IPText>
            </IPStack.Item>
          </Fragment>
        )}
      </IPStack>
    </IPStack.Item>
  );
};
